import React, { useEffect, useState } from "react";
import { Smartphone, Download, Clock, History } from "lucide-react";
import { relativeTime } from "../../utils/oversightFormat";

const titleCase = (value) =>
  (value || "")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// Platform names with non-title-case branding (titleCase would give "Ios").
const PLATFORM_LABELS = { ios: "iOS" };

const formatDeviceType = (type) => {
  if (!type) return "";
  return PLATFORM_LABELS[type.toLowerCase()] || titleCase(type);
};

// "1.2.0+93" -> "1.2.0(93)"; "1.2.0" (no build suffix) -> "1.2.0" unchanged.
const formatAppVersion = (value) => {
  if (!value) return "";
  const plusIndex = value.indexOf("+");
  if (plusIndex === -1) return value;
  const version = value.slice(0, plusIndex);
  const build = value.slice(plusIndex + 1);
  return `${version}(${build})`;
};

// Intl.DateTimeFormat construction does locale/timezone data lookup and isn't
// cheap - the live clock calls this once a second for as long as a card
// stays open, so formatters are built once per timezone and reused rather
// than reconstructed every tick. IANA timezones are a small, bounded set
// (~400), so this cache never needs eviction.
const formatterCache = new Map();
const getFormatter = (timezone) => {
  let formatter = formatterCache.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    formatterCache.set(timezone, formatter);
  }
  return formatter;
};

// Extract day/month/year/hour/minute/second/am-pm for a given IANA timezone,
// assembled manually so the output shape is exact and locale-independent
// (unlike a whole-string Intl format, which varies by browser/OS locale).
const getTimeParts = (date, timezone) => {
  const parts = getFormatter(timezone).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return {
    day: get("day"),
    month: get("month"),
    year: get("year"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
    dayPeriod: get("dayPeriod").toLowerCase(),
  };
};

const formatLiveClock = (date, timezone) => {
  try {
    const { hour, minute, second, dayPeriod } = getTimeParts(date, timezone);
    return `${hour}:${minute}:${second} ${dayPeriod}`;
  } catch {
    return null;
  }
};

const formatAbsoluteDateTime = (iso, timezone) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    const { day, month, year, hour, minute, dayPeriod } = getTimeParts(date, timezone);
    return `${day} ${month} ${year}, ${hour}:${minute} ${dayPeriod}`;
  } catch {
    return null;
  }
};

// Shared row layout: icon + label on the left, arbitrary value markup on the
// right. Value styling is left to each caller (rows like "Last seen" mix
// bold and muted text within the same value).
const InfoRow = ({ icon: Icon, label, children }) => (
  <div className="flex items-center justify-between py-3">
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
      <span className="text-sm text-gray-500">{label}</span>
    </div>
    {children}
  </div>
);

const RowValue = ({ children }) => (
  <span className="text-sm font-semibold text-gray-900">{children}</span>
);

// Its own component only because the live clock needs its own ticking state;
// the rest of the card is static per render.
const LiveClockRow = ({ timezone }) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const formatted = formatLiveClock(now, timezone);
  if (!formatted) return null;

  return (
    <InfoRow
      icon={Clock}
      label={
        <>
          Current time <span className="text-gray-400">({timezone})</span>
        </>
      }
    >
      <RowValue>{formatted}</RowValue>
    </InfoRow>
  );
};

/**
 * Read-only "Device & Activity" card for the profile card banner.
 * @param {Object} props
 * @param {{type:string,appVersion:string,osVersion:string,timezone:string,lastSeenAt:string}} props.device
 */
const DeviceActivityCard = ({ device }) => {
  if (!device) return null;

  // Only show a device label when there's a platform to attach the OS
  // version to - a bare version number with no "Android"/"iOS" prefix reads
  // as confusing on its own.
  const deviceLabel = device.type
    ? [formatDeviceType(device.type), device.osVersion].filter(Boolean).join(" ")
    : "";

  const lastSeenAbsolute = formatAbsoluteDateTime(device.lastSeenAt, device.timezone);

  // A device record can exist with every field null/empty (e.g. a device
  // that registered but never reported activity) - each row already hides
  // itself in that case, but without this check the card would still show
  // a bare "DEVICE & ACTIVITY" header with nothing underneath it.
  const hasClock = Boolean(device.timezone && formatLiveClock(new Date(), device.timezone));
  const hasAnyRow = Boolean(deviceLabel) || Boolean(device.appVersion) || hasClock || Boolean(lastSeenAbsolute);
  if (!hasAnyRow) return null;

  return (
    <div className="divide-y divide-gray-100">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider pb-3">
        Device &amp; Activity
      </h3>

      {deviceLabel && (
        <InfoRow icon={Smartphone} label="Device">
          <RowValue>{deviceLabel}</RowValue>
        </InfoRow>
      )}

      {device.appVersion && (
        <InfoRow icon={Download} label="App version">
          <RowValue>{formatAppVersion(device.appVersion)}</RowValue>
        </InfoRow>
      )}

      {device.timezone && <LiveClockRow timezone={device.timezone} />}

      {lastSeenAbsolute && (
        <InfoRow icon={History} label="Last seen">
          <span className="text-sm">
            <span className="font-semibold text-gray-900">{lastSeenAbsolute}</span>
            <span className="text-gray-400"> · {relativeTime(device.lastSeenAt)}</span>
          </span>
        </InfoRow>
      )}
    </div>
  );
};

export default DeviceActivityCard;
